import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { SingleValueFormInput } from '../../../../helpers/single-value-form-input';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';

@Component({
    selector: 'sh-form-number-complex-base',
    templateUrl: './form-number-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormNumberComplexBaseComponent)],
    standalone: true,
    imports: [ReactiveFormsModule, AsyncPipe]
})
export class ShFormNumberComplexBaseComponent extends SingleValueFormInput<number | null> implements OnInit, OnDestroy {
    public numberControl: UntypedFormControl;

    public language: string;

    @Input() id!: string;
    @Input() required = false;
    @Input() invalid = false;
    @Input() min: number;
    @Input() max: number;
    @Input() step = 1;
    @Input() describeLabelId = '';
    @Input() placeholder = '';
    @Input() indeterminateMsg = '';

    @ViewChild('elementRef', { static: true }) elementRef!: ElementRef;

    public inputType$!: Observable<'text' | 'number'>;
    public message$!: Observable<string>;

    private readonly indeterminate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly focus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly keyboardChange$: Subject<void> = new Subject<void>();

    // Injectors
    private readonly translateService: TranslateService = inject(TranslateService);

    constructor(changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);
        this.numberControl = new UntypedFormControl();

        this.initPlaceholderMessage();
    }

    ngOnInit(): void {
        this.initInputTypeStream();
        this.initListeners();

        this.unsetControlAfterInvalidKeyboardInput();

        this.language = this.translateService.currentLang;
    }

    keyboardChange(_: KeyboardEvent): void {
        this.keyboardChange$.next();
    }

    focusChange(focusEvent: FocusEvent): void {
        if (focusEvent.type === 'focus') {
            this.focus$.next(true);
        } else if (focusEvent.type === 'blur') {
            this.focus$.next(false);
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.indeterminate$.complete();
        this.focus$.complete();
        this.keyboardChange$.complete();
    }

    initListeners(): void {
        const input$: Observable<number | ''> = this.numberControl.valueChanges;

        const firstChar$: Observable<number | ''> = input$.pipe(
            filter(value => !!value),
            take(1)
        );

        firstChar$.pipe(takeUntil(this.destroy$)).subscribe(() => this.indeterminate$.next(false));

        input$.pipe(takeUntil(this.destroy$)).subscribe(value => {
            const numberValue: number = value === '' ? Number.NaN : Number(value);
            if (Number.isNaN(numberValue)) {
                this.propagateChange(new FormValue(null, FormValueState.UNSET));
            } else {
                this.propagateChange(new FormValue(numberValue, FormValueState.SET));
            }
        });
    }

    public unset(): void {
        if (this.indeterminate$.value) {
            this.indeterminate$.next(false);
        }
        if (this.numberControl.value !== null) {
            this.numberControl.setValue(null); // Traps focus if set during onBlur
        }
        this.propagateChange(new FormValue(null, FormValueState.UNSET));
    }

    public set(value: number | null): void {
        if (value === null || value === undefined) {
            this.unset();
            return;
        }
        const numberValue = Number(value);

        if (Number.isNaN(numberValue)) {
            console.error(`ShFormNumberComplexBaseComponent:: FormValue does not contain a string: ${value}`);
            this.unset();
            return;
        }

        if (this.indeterminate$.value) {
            this.indeterminate$.next(false);
        }
        this.numberControl.setValue(numberValue);
    }

    public makeIndeterminate(): void {
        this.indeterminate$.next(true);
        this.numberControl.setValue(null, { emitEvent: false });
    }

    private unsetControlAfterInvalidKeyboardInput(): void {
        const blur$: Observable<boolean> = this.focus$.pipe(filter(focus => !focus));

        this.keyboardChange$
            .pipe(
                switchMap(() => blur$),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.numberControl.value === null) {
                    this.unset();
                }
            });
    }

    private initPlaceholderMessage(): void {
        this.message$ = this.indeterminate$.pipe(
            map(isIndeterminate => (isIndeterminate ? this.indeterminateMsg : this.placeholder))
        );
    }

    private initInputTypeStream(): void {
        this.inputType$ = combineLatest([this.indeterminate$, this.focus$]).pipe(
            map(([indeterminate, focus]) => (indeterminate && !focus ? 'text' : 'number')),
            distinctUntilChanged(),
            tap(this.refocusManually.bind(this)),
            startWith('number')
        );
    }

    private refocusManually(value: 'number' | 'text'): void {
        if (value === 'number' && document.activeElement === this.nativeElement) {
            setTimeout(() => {
                // Firefox needs this, otherwise focus is ignored
                this.nativeElement.focus();
            }, 0);
        }
    }

    private get nativeElement(): HTMLInputElement {
        return this.elementRef.nativeElement as HTMLInputElement;
    }
}
